import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronDown, ArrowRight } from "lucide-react";
import { COMPANY } from "../lib/data";
import { CATEGORIES, prestationList } from "../lib/prestations";
import Logo from "./Logo";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hoverCat, setHoverCat] = useState(null);
  const [mobileCat, setMobileCat] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setHoverCat(null); }, [location.pathname]);

  const solid = scrolled || open;

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        solid ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm" : "bg-white/60 backdrop-blur-md"
      }`}
      onMouseLeave={() => setHoverCat(null)}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1">
          <Link to="/" data-testid="nav-link-accueil" className="px-3 py-2 text-sm font-semibold text-ink-soft hover:text-ink transition-colors">
            Accueil
          </Link>
          {CATEGORIES.map((c) => (
            <div key={c.slug} className="relative" onMouseEnter={() => setHoverCat(c.slug)}>
              <Link
                to={`/${c.slug}`}
                data-testid={`nav-link-${c.slug}`}
                className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-ink-soft hover:text-ink transition-colors"
              >
                {c.name} <ChevronDown className="h-3.5 w-3.5" />
              </Link>
            </div>
          ))}
          <Link to="/blog" data-testid="nav-link-blog" className="px-3 py-2 text-sm font-semibold text-ink-soft hover:text-ink transition-colors">
            Blog
          </Link>
          <a href="/#contact" data-testid="nav-link-contact" className="px-3 py-2 text-sm font-semibold text-ink-soft hover:text-ink transition-colors">
            Contact
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <a href={COMPANY.phoneHref} data-testid="nav-call-btn" className="hidden sm:inline-flex btn-primary !py-2.5 !px-4 text-sm">
            <Phone className="h-4 w-4" /> {COMPANY.phone}
          </a>
          <button data-testid="mobile-menu-toggle" className="lg:hidden p-2 rounded-lg border border-slate-200 text-ink"
            onClick={() => setOpen((o) => !o)} aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Desktop mega dropdown */}
      <AnimatePresence>
        {hoverCat && (
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            className="hidden lg:block absolute left-0 w-full bg-white border-b border-slate-200 shadow-xl"
            onMouseEnter={() => setHoverCat(hoverCat)}
          >
            <div className="max-w-7xl mx-auto px-8 py-8">
              {CATEGORIES.filter((c) => c.slug === hoverCat).map((c) => (
                <div key={c.slug} className="grid grid-cols-4 gap-8">
                  <div className="col-span-1">
                    <h3 className="font-display font-extrabold text-ink text-2xl">{c.name}</h3>
                    <p className="text-ink-soft text-sm mt-2">{c.tagline}</p>
                    <Link to={`/${c.slug}`} className="btn-link text-brand text-sm mt-4">
                      Voir la page {c.name} <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                  <div className="col-span-3 grid grid-cols-3 gap-x-6 gap-y-1">
                    {prestationList(c.slug).map((p) => (
                      <Link key={p.slug} to={`/${c.slug}/${p.slug}`}
                        className="text-sm text-ink-soft hover:text-brand py-2 border-b border-slate-100 transition-colors">
                        {p.h1.replace(/ (à Paris|d'urgence).*/, "")}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-white border-t border-slate-200 max-h-[80vh] overflow-y-auto"
          >
            <div className="px-5 py-4 flex flex-col">
              <Link to="/" className="py-3 text-lg font-semibold text-ink border-b border-slate-100">Accueil</Link>
              {CATEGORIES.map((c) => (
                <div key={c.slug} className="border-b border-slate-100">
                  <button
                    className="w-full flex items-center justify-between py-3 text-lg font-semibold text-ink"
                    onClick={() => setMobileCat(mobileCat === c.slug ? null : c.slug)}
                  >
                    {c.name}
                    <ChevronDown className={`h-5 w-5 transition-transform ${mobileCat === c.slug ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {mobileCat === c.slug && (
                      <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}
                        className="overflow-hidden">
                        <div className="pb-3 flex flex-col">
                          <Link to={`/${c.slug}`} className="py-2 text-sm font-semibold text-brand">
                            Voir tout — {c.name}
                          </Link>
                          {prestationList(c.slug).map((p) => (
                            <Link key={p.slug} to={`/${c.slug}/${p.slug}`} className="py-2 text-sm text-ink-soft">
                              {p.h1.replace(/ (à Paris|d'urgence).*/, "")}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
              <a href="/#contact" className="py-3 text-lg font-semibold text-ink border-b border-slate-100">Contact</a>
              <Link to="/blog" className="py-3 text-lg font-semibold text-ink border-b border-slate-100">Blog</Link>
              <a href={COMPANY.phoneHref} className="btn-primary mt-4 w-full">
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
